import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from 'react-router-dom';
import { showPmrDeleteConfirm, showPmrError } from '../../../utils/swalUtils';
import StethoscopeLoading from '../../../components/StethoscopeLoading/StethoscopeLoading';
import {
  announcementsGet,
  announcementsCommentsSave,
  announcementsDelete,
  CreateBlogReaction,
  GetBlogCommentsReactions,
  createBlogComment
} from 'apiServices';
import BlogTwo from './blogTwo';
import './blog.css';
import { FaEdit, FaTrash, FaThumbsUp, FaThumbsDown, FaRegThumbsUp, FaRegThumbsDown } from 'react-icons/fa';
import { Edit, Trash2 } from 'lucide-react';

import CustomSuccessModal from '../../../components/CustomSuccessModal';

// Custom error SVG for SweetAlert2 — removed; use swalUtils


const Blog = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.apiService.login);
  const token = userData?.Token || '';
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isNewBlog, setIsNewBlog] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [comment, setComment] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [comments, setComments] = useState([]);
  const [replies, setReplies] = useState([]);
  const [reactions, setReactions] = useState({ likes: 0, dislikes: 0, user_reaction: null });
  const [commentError, setCommentError] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [imageErrors, setImageErrors] = useState({}); // Track image load errors
  const [submitClicked, setSubmitClicked] = useState(false);
  const [reactionLoading, setReactionLoading] = useState(false); // Track reaction API calls

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = () => {
    setLoading(true);
    dispatch(announcementsGet({
      token,
      user_id: userData.user_id,
      announcement_id: 0,
      hospital_id: 0,
      role_id: 0
    })).unwrap()
      .then((response) => {
        console.log(response, "response")
        if (response && Array.isArray(response)) {

          response.forEach((announcement, index) => {

          });
          setAnnouncements(response);
          // Reset image errors after fetching new announcements
          setImageErrors({});
        } else {
          console.warn('Unexpected response format:', response);
          setAnnouncements([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching announcements:', error);
        setAnnouncements([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Function to handle image loading errors
  const handleImageError = (announcementId, error) => {
    // console.error(`Image failed to load for announcement ${announcementId}:`, error);
    setImageErrors(prev => ({
      ...prev,
      [announcementId]: true
    }));
  };

  // Function to handle successful image loading
  const handleImageLoad = (announcementId) => {
    setImageErrors(prev => ({
      ...prev,
      [announcementId]: false
    }));
  };

  // Function to validate a generic URL (used for Announcement Link)
  const isValidUrl = (url) => {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      return false;
    }
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch (error) {
      return false;
    }
  };

  // Function to check if image URL is valid
  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      return false;
    }

    // Check for malformed URLs that contain both server path and blob URL
    if (url.includes('/AnnouncementImages/blob:')) {
      return false;
    }

    // Check if it's a blob URL (these are temporary and won't work)
    if (url.startsWith('blob:')) {
      return false;
    }

    // Check if it's a valid URL format
    try {
      new URL(url);
      return true;
    } catch {
      // If not a valid URL, check if it's a relative path
      return url.includes('.') && (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.gif') || url.includes('.webp'));
    }
  };

  // Function to clean and fix malformed image URLs
  const cleanImageUrl = (url) => {
    if (!url || typeof url !== 'string') {
      return null;
    }

    // Handle malformed URLs that contain both server path and blob URL
    if (url.includes('/AnnouncementImages/blob:')) {
      // Extract just the server base path - this is a fallback
      // In reality, these images might not exist, but we can try
      const serverBase = url.split('/AnnouncementImages/blob:')[0];
      // console.warn('Malformed image URL detected:', url);
      // console.warn('This image cannot be displayed. Please re-upload the image.');
      return null; // Return null to show fallback
    }

    // Remove blob URLs as they're temporary
    if (url.startsWith('blob:')) {
      // console.warn('Blob URL detected - cannot display:', url);
      return null;
    }

    // Handle multiple URL duplications
    // Extract the base URL pattern and filename
    const baseUrlPattern = /https?:\/\/[^\/]+\/AnnouncementImages\//g;
    const matches = url.match(baseUrlPattern);

    if (matches && matches.length > 1) {
      // Multiple base URLs found - extract the filename from the end
      const lastBaseUrlIndex = url.lastIndexOf('AnnouncementImages/');
      if (lastBaseUrlIndex !== -1) {
        const filename = url.substring(lastBaseUrlIndex + 'AnnouncementImages/'.length);
        // Reconstruct with just one base URL
        const cleanBaseUrl = matches[0]; // Take the first occurrence as the base
        url = cleanBaseUrl + filename;
      }
    }

    // Fix backslashes in URLs - replace \\ with /
    let cleanedUrl = url.replace(/\\\\/g, '/').replace(/\\/g, '/');

    // console.log(cleanedUrl, "cleaned url");
    return cleanedUrl;
  };

  const fetchCommentsAndReactions = (announcement_id) => {
    console.log('Fetching comments and reactions for blog:', announcement_id, 'user:', userData.user_id);

    dispatch(GetBlogCommentsReactions({
      blogId: announcement_id,
      loggedinUserId: userData.user_id,
      token
    }))
      .then((response) => {
        console.log('GetBlogCommentsReactions response:', response);

        if (response?.payload) {
          const fetchedComments = Array.isArray(response.payload.Table) ? response.payload.Table : [];
          const fetchedReplies = Array.isArray(response.payload.Table1) ? response.payload.Table1 : [];

          // Handle user reaction data from Table2
          let userReactionData = { likes: false, dislikes: false, reaction_id: null };
          if (response.payload.Table2 && response.payload.Table2.length > 0) {
            const userReaction = response.payload.Table2[0];
            userReactionData = {
              likes: userReaction.likes || false,
              dislikes: userReaction.dislikes || false,
              reaction_id: userReaction.reaction_id || null
            };
          }

          // Handle total counts from Table3
          let totalCounts = { Totallikes: 0, Totaldislikes: 0 };
          if (response.payload.Table3 && response.payload.Table3.length > 0) {
            totalCounts = response.payload.Table3[0];
          }

          console.log('User reaction data:', userReactionData);
          console.log('Total counts:', totalCounts);

          setComments(fetchedComments);
          setReplies(fetchedReplies);
          setReactions({
            likes: totalCounts.Totallikes || 0,
            dislikes: totalCounts.Totaldislikes || 0,
            user_reaction: userReactionData.likes ? 'like' : (userReactionData.dislikes ? 'dislike' : null),
            reaction_id: userReactionData.reaction_id
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching comments and reactions:', error);
      });
  };
  const handleReadMore = (announcement) => {
    setSelectedAnnouncement(announcement);
    fetchCommentsAndReactions(announcement.announcement_id);
    setIsNewBlog(false);
    setCommentError(false);
    setSubmitClicked(false);
    setIsReplying(false);
    setReplyTo(null);
    setComment('');
    setReactionLoading(false);
  };

  const handleNewBlog = () => {
    setEditingAnnouncement(null);
    setIsNewBlog(true);
    setSelectedAnnouncement(null);
    setCommentError(false);
  };

  const handleBackToList = () => {
    setSelectedAnnouncement(null);
    setComments([]);
    setReplies([]);
    setReactions({ likes: 0, dislikes: 0, user_reaction: null });
    setCommentError(false);
    setSubmitClicked(false);
    setIsReplying(false);
    setReplyTo(null);
    setComment('');
    setReactionLoading(false);
  };

  const handleFormClose = () => {
    setIsNewBlog(false);
    setEditingAnnouncement(null);
    fetchAnnouncements();
    handleBackToList();
    setCommentError(false);
  };

  // Callback function to refresh announcements immediately after successful updates
  const handleAnnouncementUpdate = () => {
    fetchAnnouncements();
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    setSubmitClicked(true);

    if (!comment.trim()) {
      setCommentError(true);
      return;
    }

    const payload = {
      blogId: selectedAnnouncement.announcement_id,
      createdBy: userData.user_id,
      comments: comment,
      parentCommentId: isReplying ? replyTo.comment_id : 0,
      token: token
    };

    dispatch(createBlogComment(payload)).then(() => {
      setComment('');
      setIsReplying(false);
      setReplyTo(null);
      setCommentError(false);
      setSubmitClicked(false);
      fetchCommentsAndReactions(selectedAnnouncement.announcement_id); // Refresh comments
    });
  };

  const handleReply = (comment) => {
    setIsReplying(true);
    setReplyTo(comment);
    setComment('');
    setCommentError(false);
  };

  const handleCancelReply = () => {
    setIsReplying(false);
    setReplyTo(null);
    setComment('');
    setCommentError(false);
  };

  const handleLikeReaction = () => {
    // Prevent multiple clicks while processing
    if (reactionLoading) return;

    setReactionLoading(true);

    // Get current reaction ID if exists
    const currentReactionId = reactions.reaction_id || null;

    // Determine if we should toggle off or set like
    const isCurrentlyLiked = reactions.user_reaction === 'like';

    const payload = {
      blogId: selectedAnnouncement.announcement_id,
      createdBy: userData.user_id,
      likes: !isCurrentlyLiked, // Toggle: if currently liked, set to false; if not liked, set to true
      dislikes: false, // Always set dislikes to false when clicking like
      token: token
    };

    // Add reactionId if it exists (for updating existing reaction)
    if (currentReactionId) {
      payload.reactionId = currentReactionId;
    }

    console.log('Like reaction payload:', payload);
    console.log('Current user reaction:', reactions.user_reaction);

    // Make API call
    dispatch(CreateBlogReaction(payload))
      .then((response) => {
        console.log('Like reaction response:', response);
        // Refresh from server to get updated counts
        fetchCommentsAndReactions(selectedAnnouncement.announcement_id);
      })
      .catch((error) => {
        console.error('Error updating like reaction:', error);
      })
      .finally(() => {
        setReactionLoading(false);
      });
  };

  const handleDislikeReaction = () => {
    // Prevent multiple clicks while processing
    if (reactionLoading) return;

    setReactionLoading(true);

    // Get current reaction ID if exists
    const currentReactionId = reactions.reaction_id || null;

    // Determine if we should toggle off or set dislike
    const isCurrentlyDisliked = reactions.user_reaction === 'dislike';

    const payload = {
      blogId: selectedAnnouncement.announcement_id,
      createdBy: userData.user_id,
      likes: false, // Always set likes to false when clicking dislike
      dislikes: !isCurrentlyDisliked, // Toggle: if currently disliked, set to false; if not disliked, set to true
      token: token
    };

    // Add reactionId if it exists (for updating existing reaction)
    if (currentReactionId) {
      payload.reactionId = currentReactionId;
    }

    console.log('Dislike reaction payload:', payload);
    console.log('Current user reaction:', reactions.user_reaction);

    // Make API call
    dispatch(CreateBlogReaction(payload))
      .then((response) => {
        console.log('Dislike reaction response:', response);
        // Refresh from server to get updated counts
        fetchCommentsAndReactions(selectedAnnouncement.announcement_id);
      })
      .catch((error) => {
        console.error('Error updating dislike reaction:', error);
      })
      .finally(() => {
        setReactionLoading(false);
      });
  };

  const isBlogOwner = (announcement) => {
    if (!announcement || !userData) return false;
    const userId = userData.user_id ?? userData.UserId ?? userData.UserID;
    const ownerId = announcement.created_by
      ?? announcement.Created_By
      ?? announcement.createdBy
      ?? announcement.CreatedByID
      ?? announcement.user_id;
    if (userId != null && ownerId != null && String(userId) === String(ownerId)) return true;

    const role = String(userData.user_type_name || userData.type_name || '').toLowerCase();
    if (role === 'admin') return true;

    const author = String(announcement.CreatedBy || announcement.created_by_name || '').trim().toLowerCase();
    const fullName = [userData.first_name, userData.last_name].filter(Boolean).join(' ').trim().toLowerCase();
    const userName = String(userData.user_name || userData.UserName || '').trim().toLowerCase();
    return Boolean(author) && (author === fullName || author === userName);
  };

  const handleDelete = (announcement_id) => {
    showPmrDeleteConfirm({
      message: 'You want to delete this blog? This action cannot be undone.',
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          announcement_id: announcement_id,
          deleted_by: userData.user_id,
          token: token
        };
        dispatch(announcementsDelete(payload))
          .unwrap()
          .then((response) => {
            // If we reach here, the deletion was successful
            setSuccessMessage(response.return_message || 'Your blog has been deleted successfully!');
            setShowSuccessModal(true);
            handleBackToList();
            fetchAnnouncements(); // Refresh the announcements list
          })
          .catch((error) => {
            console.error('Delete error:', error);
            showPmrError(error || 'Failed to delete the blog. Please try again.');
          });
      }
    });
  };

  const handleEdit = () => {
    setEditingAnnouncement(selectedAnnouncement);
    setIsNewBlog(true);
  }

  // Get replies for a specific comment
  const getRepliesForComment = (commentId) => {
    return replies.filter(reply => reply.parentcomment_id === commentId);
  };

  // Render comments with nested replies
  const renderComments = (commentList, parentId = 0, margin = 0) => {
    return commentList
      .filter(c => {
        // Handle null/undefined parentcomment_id for main comments
        if (parentId === 0) {
          return !c.parentcomment_id || c.parentcomment_id === 0 || c.parentcomment_id === null;
        }
        return c.parentcomment_id === parentId;
      })
      .map(c => (
        <div key={c.comment_id} style={{ marginLeft: `${margin}px` }} className="comment-item">
          <div className="comment-author">{c.user_name}</div>
          <div className="d-flex justify-content-between align-items-start">
            <div className="comment-text">{c.comments}</div>
            <button className="comment-reply-btn" onClick={() => handleReply(c)}>Reply</button>
          </div>
          {renderComments(commentList, c.comment_id, margin + 20)}
        </div>
      ));
  };

  return (
    <div >
      {isNewBlog ? (
        <BlogTwo
          announcementToEdit={editingAnnouncement}
          onFinish={handleFormClose}
          onUpdate={handleAnnouncementUpdate}
        />
      ) : selectedAnnouncement ? (
        <div className="row justify-content-center">
          <div className="col-md-5">
            <div className="bg-white rounded shadow-sm p-4">
              <div className="mb-4">
                <button
                  className="btn btn-primary btn-sm back-button"
                  onClick={handleBackToList}
                >
                  Back
                </button>
              </div>

              <div className="d-flex justify-content-between align-items-start mb-3">
                <div style={{ flexGrow: 1 }}>
                  <h4 className="blog-post-title">{selectedAnnouncement.title}</h4>
                  <p className="blog-post-subtitle">{selectedAnnouncement.sub_title}</p>
                </div>
                {isBlogOwner(selectedAnnouncement) && (
                  <div className="d-flex align-items-center" style={{ gap: '1.5rem', paddingTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="circular-btn circular-btn-edit"
                      onClick={() => handleEdit(selectedAnnouncement.announcement_id)}
                    >
                      <Edit size={22} />
                    </button>
                    <button
                      type="button"
                      className="circular-btn circular-btn-delete"
                      onClick={() => handleDelete(selectedAnnouncement.announcement_id)}
                    >
                      <Trash2 size={22} />
                    </button>
                  </div>
                )}
              </div>

              {(() => {
                const cleanedUrl = cleanImageUrl(selectedAnnouncement.image_url);
                return cleanedUrl && isValidImageUrl(cleanedUrl) && !imageErrors[selectedAnnouncement.announcement_id] ? (
                  <div className="mb-3" style={{ width: '100%', overflow: 'hidden' }}>
                    <img
                      src={cleanedUrl}
                      className="img-fluid"
                      alt={selectedAnnouncement.title}
                      style={{
                        maxHeight: '400px',
                        width: '100%',
                        objectFit: 'contain',
                        display: 'block',
                        // margin: '0 auto'
                      }}
                      onLoad={() => handleImageLoad(selectedAnnouncement.announcement_id)}
                      onError={(e) => handleImageError(selectedAnnouncement.announcement_id, e)}
                    />
                  </div>
                ) : null;
              })()}

              <div className="blog-description">
                <div dangerouslySetInnerHTML={{ __html: selectedAnnouncement.description }} />
              </div>

              {selectedAnnouncement.card_link && isValidUrl(selectedAnnouncement.card_link) && (
                <div className="mt-3 mb-2">
                  <a
                    href={selectedAnnouncement.card_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--pmr-primary)',
                      fontFamily: 'Arial, sans-serif',
                      fontWeight: 'bold',
                      textDecoration: 'none'
                    }}
                  >
                    Announcement Link
                  </a>
                </div>
              )}

              <div className="text-end mb-4">
                <div className="blog-author-date">
                  - by {selectedAnnouncement.CreatedBy} on {selectedAnnouncement.created_on}
                </div>
              </div>

              <div className="comments-section">
                <h6 className="comments-heading">Comments</h6>
                {(comments.length > 0 || replies.length > 0) && (
                  <div className="comment-container">
                    {renderComments([...comments, ...replies])}
                  </div>
                )}

                <div className="mt-3">
                  <textarea
                    className={`form-control form-control-sm mb-3${commentError ? ' error-border' : ''}`}
                    placeholder={isReplying ? `Replying to ${replyTo?.user_name}...` : 'Write a comment...'}
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                      if (e.target.value.trim()) {
                        setCommentError(false);
                      }
                    }}
                    rows="3"
                  />
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex" style={{ gap: '1.5rem' }}>
                      <button
                        className={`btn btn-link p-0 reaction-btn ${reactions.user_reaction === 'like' ? 'active-like' : ''} ${reactionLoading ? 'loading' : ''}`}
                        onClick={handleLikeReaction}
                        style={{ textDecoration: 'none', transition: 'all 0.2s ease' }}
                        disabled={reactionLoading}
                        title={reactionLoading ? 'Processing...' : (reactions.user_reaction === 'like' ? 'Remove like' : 'Like this post')}
                      >
                        <div className="d-flex flex-column align-items-center">
                          {reactions.user_reaction === 'like'
                            ? <FaThumbsUp className="reaction-icon liked" />
                            : <FaRegThumbsUp className="reaction-icon" />}
                          <span className="reaction-count">{reactions.likes}</span>
                        </div>
                      </button>
                      <button
                        className={`btn btn-link p-0 reaction-btn ${reactions.user_reaction === 'dislike' ? 'active-dislike' : ''} ${reactionLoading ? 'loading' : ''}`}
                        onClick={handleDislikeReaction}
                        style={{ textDecoration: 'none', transition: 'all 0.2s ease' }}
                        disabled={reactionLoading}
                        title={reactionLoading ? 'Processing...' : (reactions.user_reaction === 'dislike' ? 'Remove dislike' : 'Dislike this post')}
                      >
                        <div className="d-flex flex-column align-items-center">
                          {reactions.user_reaction === 'dislike'
                            ? <FaThumbsDown className="reaction-icon disliked" />
                            : <FaRegThumbsDown className="reaction-icon" />}
                          <span className="reaction-count">{reactions.dislikes}</span>
                        </div>
                      </button>
                    </div>
                    <div>
                      {isReplying ? (
                        <>
                          <button className="btn btn-primary btn-sm me-2 add-comment-btn" onClick={handleCommentSubmit}>
                            Reply to {replyTo?.user_name}
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={handleCancelReply}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button className="btn btn-primary btn-sm back-button" style={commentError ? { border: '1px solid #dc3545 !important; box-shadow: none !important;' } : {}} onClick={handleCommentSubmit}>
                          Add Comment
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className='bg-white rounded shadow-lg '>
          <div className='container p-2'>
            <div className="d-flex justify-content-end mb-3">
              <button className="btn btn-primary mt-6 back-button" onClick={handleNewBlog}>
                New Blog
              </button>
            </div>
            <div className="row justify-content-center">
              {loading ? (
                <StethoscopeLoading message="Loading..." />
              ) : (
                announcements.map((announcement, index) => {
                  // Heuristic to determine layout style. This can be replaced with a more robust flag from the API if available.
                  const isBannerPost = announcement.image_url && (announcement.title?.length || 0) < 5;

                  return (
                    <div key={announcement.announcement_id || index} className="col-md-6 mb-4" style={{ width: '35%' }} >
                      <div className="card shadow-sm h-100 blog-card">
                        {/* Image Section */}
                        {(() => {
                          const cleanedUrl = cleanImageUrl(announcement.image_url);
                          return cleanedUrl && isValidImageUrl(cleanedUrl) && !imageErrors[announcement.announcement_id] ? (
                            <div className="blog-image-container">
                              <img
                                src={cleanedUrl}
                                className="blog-card-image"
                                alt={announcement.title || ""}
                                onLoad={() => handleImageLoad(announcement.announcement_id)}
                                onError={(e) => handleImageError(announcement.announcement_id, e)}
                              />
                            </div>
                          ) : null;
                        })()}

                        {/* Content Section */}
                        <div className="card-body blog-card-content">
                          <div className="blog-content-wrapper">
                            <p className="blog-title">{announcement.title || "No Title"}</p>
                            <p className="blog-subtitle">{announcement.sub_title || ""}</p>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="blog-author">
                              {announcement.CreatedBy}
                            </div>
                            <div className="blog-read-more">
                              <button
                                className="btn btn-link p-0 blog-read-more-btn"
                                onClick={() => handleReadMore(announcement)}
                              >
                                READ MORE &gt;
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      )}

      <CustomSuccessModal
        show={showSuccessModal}
        onHide={() => { setShowSuccessModal(false); setSuccessMessage(''); }}
        message={successMessage}
      />

    </div>
  );
};
export default Blog;
